import { Component, OnInit } from '@angular/core';
import { PagingContainerModel } from 'src/app/model/paging-container.model';
import { ProcessSummaryModel } from 'src/app/model/process-summary.model';
import { PagingModel } from 'src/app/model/paging.model';

import swal from 'sweetalert2';
import { SearchParamsModel } from 'src/app/model/search-params.model';
import { environment } from 'src/environments/environment';
import { ExportService } from 'src/app/service/export.services';
import { WorkflowGatewayService } from 'src/app/service/workflow-gateway.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthenticationService } from 'src/app/service/auth.service';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BackdropService } from 'src/app/service/backdrop.service';
import { AttributeModel } from '../../dynamic/model/attribute.model';
import { filter } from 'rxjs/operators';
import { DesignService } from 'src/app/service/design.service';

@Component({
  selector: "app-process-list",
  templateUrl: "./process-list.component.html",
  styleUrls: ["./process-list.component.scss"],
})
export class ProcessListComponent implements OnInit {
  pagingModel: PagingModel;
  processes: ProcessSummaryModel[];
  countPage: number = 1;
  currentPage: number = 1;
  totalPages: number | null = null;
  totalItems: number | null = null;
  isCountLoading = false;
  hasNextPage = false;
  hasPrevPage = false;
  totalColumns: number[];
  searchParams: SearchParamsModel = new SearchParamsModel();
  listExcel: any[];
  itemExcel: any;
  showModalPrint: boolean;
  printProcessId: string;
  itemsAttr: AttributeModel[];
  selectedPrintItems: string[];
  sourceUrl: string;
  isCatalogView: boolean = false;
  isLoading: boolean = false;

  constructor(
    private exportService: ExportService,
    private workflowService: WorkflowGatewayService,
    private authService: AuthenticationService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private route: ActivatedRoute,
    private backdrop: BackdropService,
    private designService: DesignService,
  ) {}

  ngOnInit() {
    this.setSearchParams();
    this.totalColumns = [0, 1, 2];

    setTimeout(() => window.scrollTo(0, 0), 500);

    this.resolveViewMode();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.resolveViewMode());

  }

  private setSearchParams() {
    this.route.params.subscribe(params => {
      this.searchParams.policyId = params.policyId ?? null;
      this.searchParams.policyTypeId = params.typeId ?? null;

      const key = this.getSearchParamsKey();

      if (!sessionStorage.getItem(key)) {
        const initialSearchParams: SearchParamsModel = {
          policyId: this.searchParams.policyId,
          policyTypeId: this.searchParams.policyTypeId,
          attributes: [],
          nome: '',
          orderDesc: false,
        };
        sessionStorage.setItem(key, JSON.stringify(initialSearchParams));
      }

      this.searchParams = JSON.parse(sessionStorage.getItem(key) || '{}');
      this.totalItems = null;
      this.totalPages = null;
      this.hasNextPage = false;
      this.hasPrevPage = false;
      this.loadCountFromStorage();
      this.loadProcesses();
    });
  }

  private getSearchParamsKey(): string {
    const id = this.getPolicyOrTypeId();
    const prefix = this.searchParams.policyId ? 'policy' : 'type';
    return `searchParams_${prefix}_${id}`;
  }

  private getPolicyOrTypeId(): string {
    const value = this.searchParams.policyId ? `policy/${this.searchParams.policyId}` : `type/${this.searchParams.policyTypeId}`;
    const match = value.match(/(policy|type)\/(.+)/);
    return match ? match[2] : '';
  }

  private getCountStorageKey(): string | null {
    const id = this.getPolicyOrTypeId();
    if (!id) { return null; }
    const prefix = this.searchParams.policyId ? 'policy' : 'type';
    return `processCount_${prefix}_${id}`;
  }

  private getPageStorageKey(): string {
    const id = this.getPolicyOrTypeId();
    if (!id) { return 'pageNumber'; }
    const prefix = this.searchParams.policyId ? 'policy' : 'type';
    return `pageNumber_${prefix}_${id}`;
  }

  private loadCountFromStorage(): void {
    const key = this.getCountStorageKey();
    if (!key) { return; }
    const stored = sessionStorage.getItem(key);
    if (!stored) { return; }
    try {
      const count = JSON.parse(stored);
      this.totalItems = count.totalItems ?? null;
      this.totalPages = count.totalPages ?? null;
    } catch {
      this.totalItems = null;
      this.totalPages = null;
    }
  }

  private saveCountToStorage(): void {
    if (this.totalItems === null) { return; }
    const key = this.getCountStorageKey();
    if (!key) { return; }
    sessionStorage.setItem(key, JSON.stringify({
      totalItems: this.totalItems,
      totalPages: this.totalPages
    }));
  }

  private clearCountFromStorage(): void {
    const key = this.getCountStorageKey();
    if (key) { sessionStorage.removeItem(key); }
  }

  private resolveViewMode(): void {
    try {
      const currentMenu = this.designService.getCurrentMenuConfig();
      const resultType = currentMenu?.options?.find(o => o.key?.toLowerCase() === 'resulttype')?.value?.toLowerCase();
      this.isCatalogView = resultType === 'catalog';
    } catch {
      this.isCatalogView = false;
    }
  }

  public async doExport(): Promise<void> {
    const key = this.getSearchParamsKey();
    if (sessionStorage.getItem(key)) {
      this.searchParams = JSON.parse(sessionStorage.getItem(key));
    }

    if (!this.searchParams.policyId && !this.searchParams.policyTypeId && this.processes.length > 0) {
      this.searchParams.policyId = this.processes[0].policyId;
    }

    if (this.searchParams.policyId || this.searchParams.policyTypeId) {
      this.spinner.show();
      await this.exportService.exportAsync(this.searchParams);
      this.spinner.hide();
    } else {
      swal.fire("Por favor", "Selecione um tipo de cadastro para exportar.", "warning");
    }
  }

  doSearch() {
    localStorage.removeItem(this.getPageStorageKey());
    this.totalItems = null;
    this.totalPages = null;
    this.hasNextPage = false;
    this.hasPrevPage = false;
    this.clearCountFromStorage();
    this.loadProcesses();
  }

  getColumnTitle(index: number): string {
    if(!this.processes || this.processes.length == 0) {
      return this.getDefaultTitle(index);
    }

    let firstItemAttrs = this.processes[0].attributes;

    if(!firstItemAttrs[index] || !firstItemAttrs[index].options) {
      return this.getDefaultTitle(index);
    }

    let titleOpt = firstItemAttrs[index].options
      .find(opt => opt.key.toLowerCase() == "title");

    return titleOpt ? titleOpt.value : firstItemAttrs[index].key;
  }

  private getDefaultTitle(index: number): string {
    if(index == 0) {
      return "Nome";
    } else if(index == 1) {
      return "Tipo";
    }

    return "Nome";
  }

  calculateTotalColumns() {
    if (this.processes && this.processes.length > 0) {
      const attributeCount = this.processes[0].attributes.length;
      this.totalColumns = Array.from({ length: attributeCount }, (_, i) => i);
    } else {
      this.totalColumns = [];
    }
  }

  loadProcesses(pagina?: number, showLoading: boolean = true) {
    if (showLoading) {
      this.spinner.show();
    }

    this.isLoading = true;
    this.processes = [];

    const key = this.getSearchParamsKey();
    if (sessionStorage.getItem(key)) {
      this.searchParams = JSON.parse(sessionStorage.getItem(key));
    }

    this.searchParams.orderDesc = false;

    if (!pagina) {
      const pageKey = this.getPageStorageKey();
      const stored = localStorage.getItem(pageKey);
      if (stored) { pagina = +stored; }
    }

    this.sourceUrl = null

    this.workflowService
      .getSummary(pagina, environment.processPageSize, {
        searchParams: this.searchParams,        
      },
      this.sourceUrl   
      )
      .subscribe(
        (pagingContainer: PagingContainerModel) => {
          if (pagingContainer.items.length == 0 && pagina > 1) {
            this.mudarPagina(1, false);
          } else {
            this.processes = pagingContainer.items;
            this.pagingModel = pagingContainer.paging;
            const serverPageNumber = this.pagingModel?.pageNumber ?? this.currentPage ?? 1;
            this.currentPage = serverPageNumber;
            this.hasNextPage = (pagingContainer.links || []).some(l => l.rel === 'nextPage');
            this.hasPrevPage = (pagingContainer.links || []).some(l => l.rel === 'previousPage');
            this.countPage = Math.max(Math.floor((serverPageNumber - 1) / 15) * 15 + 1, 1);
            this.calculateTotalColumns();
            this.spinner.hide();
            this.isLoading = false;
          }
        },
        (error) => this.handleHttpError(error)
      );
  }

  mudarPagina(pageNumber: number, showLoading: boolean = true) {
    if (!pageNumber) {
      pageNumber = 1;
    }

    this.currentPage = pageNumber;
    localStorage.setItem(this.getPageStorageKey(), pageNumber.toString());
    this.loadProcesses(pageNumber, showLoading);
  }

  lastPages() {
    if (this.totalPages !== null && this.totalPages > 0) {
      const last = this.totalPages;
      const start = Math.max(last - 14, 1);
      this.currentPage = last;
      this.countPage = start;
      localStorage.setItem(this.getPageStorageKey(), this.currentPage.toString());
      this.loadProcesses(this.currentPage, true);
    }
  }

  firstPages() {
    this.currentPage = 1;
    this.countPage = this.currentPage;
    localStorage.setItem(this.getPageStorageKey(), this.currentPage.toString());
    this.loadProcesses(this.currentPage, true);
  }

  nextPages(currentPage: number) {
    if (this.totalPages !== null && this.totalPages > 0) {
      const nextStart = this.countPage + 15;
      if (nextStart <= this.totalPages) {
        this.currentPage = nextStart;
        this.countPage = nextStart;
        localStorage.setItem(this.getPageStorageKey(), this.currentPage.toString());
        this.loadProcesses(this.currentPage, true);
      }
    } else if (this.hasNextPage) {
      this.mudarPagina(this.currentPage + 1, true);
    }
  }

  previousPages(currentPage: number) {
    if (this.countPage > 1) {
      const prevStart = Math.max(this.countPage - 15, 1);
      this.currentPage = prevStart;
      this.countPage = prevStart;
      localStorage.setItem(this.getPageStorageKey(), this.currentPage.toString());
      this.loadProcesses(this.currentPage, true);
    }
  }

  obterPaginas(): number[] {
    const pageNumbers: number[] = [];
    if (this.pagingModel && this.totalPages !== null && this.totalPages > 0) {
      for (let index = this.countPage; index <= this.countPage + 14; index++) {
        if (index <= this.totalPages && index >= 1) {
          pageNumbers.push(index);
        }
      }
    }
    return pageNumbers;
  }

  isActive(numero: number) {
    return this.pagingModel ? numero == this.pagingModel.pageNumber : false;
  }

  onValueChange(value: any) {
    if (value.target.checked) {
      this.selectedPrintItems.push(value.target.value);
    } else {
      const indexRemove = this.selectedPrintItems.indexOf(value.target.value);
      this.selectedPrintItems.splice(indexRemove, 1);
    }
  }

  sendPrint() {
    if (this.selectedPrintItems.length < 1) {
      swal.fire({
        title: "Atenção",
        text: "Favor selecionar um item para impressão.",
        icon: "error",
        showCancelButton: false,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Fechar",
      });
    } else {
      window.open("/print?processId=" + this.printProcessId + "&procedimentos=" + this.selectedPrintItems.toString(), "_blank");
      this.toggleModalPrint();
    }
  }

  toggleModalPrint(processId: string = null) {
    if (processId) {
      this.printProcessId = processId;
      const processList = this.processes.find(p => p.id == processId);
      this.itemsAttr = processList.attributes.find(attr => attr.type.toLowerCase() == "items").attributes;
      this.selectedPrintItems = this.itemsAttr.map(i => i.attributes[1].value);
    }

    this.showModalPrint = !this.showModalPrint;

    if (this.showModalPrint) {
      this.backdrop.show();
    } else {
      this.backdrop.hide();
    }
  }

  loadCount(): void {
    if (this.isCountLoading) {
      return;
    }
    this.isCountLoading = true;
    this.workflowService
      .getCount(environment.processPageSize, { searchParams: this.searchParams }, this.sourceUrl)
      .subscribe(
        (countResult) => {
          this.totalItems = countResult.totalItems;
          this.totalPages = countResult.totalPages;
          this.isCountLoading = false;
          this.saveCountToStorage();
        },
        () => {
          this.isCountLoading = false;
        }
      );
  }

  updateList() {
    this.loadProcesses();
  }

  private handleHttpError(error: any) {
    this.spinner.hide();
    this.isLoading = false;

    if (error.status == 401) {
      this.authService.logout();
      this.router.navigate(['login']);
    } else if (error.status == 403) {
      swal.fire("Atenção", "Este usuário não tem permissão para acessar este conteúdo.")
        .then(() => this.router.navigate([""]).then(() => swal.close()));
    } else if (error.message) {
      swal.fire("Atenção", error.message, "error").then(() => {
        if (error.goBackToHome) {
          this.router.navigate([""]).then(() => swal.close());
        }
      });
    } else {
      swal.fire("Atenção", "Algo errado aconteceu; Por favor, tente novamente.", "error");
    }
  }
}
